/**
 * 🪞 WardrobePage — Empty Dragonious hero equipment canvas.
 *
 * Owner directive (Oct 2026): an empty wardrobe screen with no hero,
 * no gear, no text, no labels — a template that Roc will layer later.
 *
 *   Rich dark background · Bronze / copper / gold / royal purple /
 *   crimson palette · tribal flame patterns · phoenix-flame accents ·
 *   glossy MMO game UI. Central empty pedestal, two vertical columns
 *   of empty square slots with ornate gold borders, a tab bar across
 *   the top, a wide rating bar across the bottom, a round wardrobe
 *   button at the bottom right.
 *
 * Deliberately zero text — "blank labels" per owner spec.
 */
import Header from "@/components/Header";

const SLOTS = ["s1", "s2", "s3", "s4", "s5"];

export default function WardrobePage() {
  return (
    <div
      className="relative min-h-screen w-full overflow-hidden text-amber-100 bg-[#0b0608]"
      data-testid="wardrobe-page"
      style={{
        backgroundImage:
          "radial-gradient(ellipse at 50% 40%, #3a1808 0%, #1a0a08 55%, #0b0608 100%)",
      }}
    >
      {/* 🔥 tribal flame grain + crimson glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 85%, rgba(220,38,38,0.35) 0px, transparent 55%)," +
            "radial-gradient(circle at 85% 10%, rgba(109,40,217,0.25) 0px, transparent 55%)",
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 mix-blend-overlay opacity-[0.12]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><defs><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter></defs><rect width='120' height='120' filter='url(%23n)' opacity='0.6'/></svg>\")",
        }}
      />

      <Header />

      <main className="relative mx-auto max-w-6xl px-4 pt-24 pb-28">
        {/* ─── Top tab bar (blank) ─────────────────────────────── */}
        <div
          className="mb-8 flex items-center gap-2 overflow-x-auto"
          data-testid="wardrobe-tabs"
        >
          {[0, 1, 2, 3, 4].map((i) => (
            <button
              key={i}
              aria-label=""
              data-testid={`wardrobe-tab-${i}`}
              className={
                "shrink-0 h-11 w-24 rounded-md border-2 transition-all " +
                (i === 0
                  ? "border-amber-400/80 bg-gradient-to-b from-amber-700/40 to-amber-900/40 shadow-[inset_0_1px_0_rgba(255,220,160,0.5)]"
                  : "border-amber-900/60 bg-gradient-to-b from-neutral-900/60 to-black/70 hover:border-amber-700/70")
              }
            />
          ))}
        </div>

        {/* ─── Pedestal + two columns of empty slots ─────────────── */}
        <div className="relative grid grid-cols-[72px_1fr_72px] gap-6 md:gap-10">
          {/* Left slot column */}
          <SlotColumn testidPrefix="wardrobe-slot-left" />

          {/* Central empty pedestal */}
          <Pedestal />

          {/* Right slot column */}
          <SlotColumn testidPrefix="wardrobe-slot-right" />
        </div>

        {/* ─── Wide rating bar (blank) ─────────────────────────── */}
        <div
          className="mt-12 h-12 rounded-xl border-2 border-amber-700/60 bg-gradient-to-r from-amber-950/70 via-black/50 to-rose-950/60 relative overflow-hidden"
          data-testid="wardrobe-rating-bar"
        >
          <div
            aria-hidden
            className="absolute inset-y-0 left-0 w-0 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-600"
          />
          {/* glossy sheen */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/10 via-transparent to-black/40"
          />
          {/* ornate end caps */}
          <div className="absolute inset-y-0 left-0 w-12 border-r-2 border-amber-700/60 bg-gradient-to-br from-amber-700/40 to-amber-950/60 flex items-center justify-center">
            <div className="h-6 w-6 rounded-full border-2 border-amber-400/70 bg-gradient-to-br from-amber-500 to-rose-700 shadow-[inset_0_1px_0_rgba(255,220,160,0.6)]" />
          </div>
          <div className="absolute inset-y-0 right-0 w-12 border-l-2 border-amber-700/60 bg-gradient-to-bl from-amber-700/40 to-amber-950/60 flex items-center justify-center">
            <div className="h-6 w-6 rounded-full border-2 border-amber-400/70 bg-gradient-to-br from-amber-500 to-rose-700 shadow-[inset_0_1px_0_rgba(255,220,160,0.6)]" />
          </div>
        </div>

        {/* ─── Round wardrobe button, bottom-right ─────────────── */}
        <button
          aria-label=""
          data-testid="wardrobe-button"
          className="group fixed bottom-6 right-6 h-16 w-16 rounded-full border-[3px] border-amber-400/70 bg-gradient-to-br from-amber-500 via-amber-700 to-rose-700 shadow-[0_10px_30px_rgba(190,40,30,0.45),inset_0_1px_0_rgba(255,230,180,0.7)] hover:scale-[1.06] active:scale-95 transition-all overflow-hidden"
        >
          {/* flame etch */}
          <div
            aria-hidden
            className="absolute inset-1 rounded-full border border-amber-200/40"
          />
          <div
            aria-hidden
            className="absolute inset-0 rounded-full bg-gradient-to-b from-white/25 via-transparent to-black/40 group-hover:from-white/35"
          />
        </button>

        {/* 🛡️ Oct 2026 — Dragonious-styled sibling button that opens the
            full data-driven Armory. Keeps the canvas text-free while
            tying this page back to the gear screen the player uses to
            actually equip stuff. Mirror of the bottom-right wardrobe
            orb, placed top-left so the two corners frame the pedestal. */}
        <a
          href="/armory"
          aria-label="Open full Armory"
          data-testid="wardrobe-armory-link"
          className="group fixed top-20 left-6 h-14 w-14 rounded-full border-[3px] border-amber-400/70 bg-gradient-to-br from-rose-700 via-amber-700 to-amber-500 shadow-[0_8px_24px_rgba(190,40,30,0.45),inset_0_1px_0_rgba(255,230,180,0.7)] hover:scale-[1.06] active:scale-95 transition-all overflow-hidden flex items-center justify-center z-30"
        >
          <div
            aria-hidden
            className="absolute inset-1 rounded-full border border-amber-200/40"
          />
          <div
            aria-hidden
            className="absolute inset-0 rounded-full bg-gradient-to-b from-white/25 via-transparent to-black/40 group-hover:from-white/35"
          />
          <span className="relative text-lg drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">🛡️</span>
        </a>
      </main>
    </div>
  );
}

// ─── Pieces ────────────────────────────────────────────────────────
function SlotColumn({ testidPrefix }) {
  return (
    <div className="flex flex-col gap-4">
      {SLOTS.map((id, idx) => (
        <SlotTile key={id} testid={`${testidPrefix}-${idx}`} />
      ))}
    </div>
  );
}

function SlotTile({ testid }) {
  return (
    <div
      data-testid={testid}
      aria-label=""
      className="relative aspect-square rounded-md border-2 border-amber-500/70 bg-gradient-to-br from-amber-950/60 via-neutral-950 to-black shadow-[inset_0_1px_0_rgba(255,220,160,0.3),0_6px_18px_rgba(0,0,0,0.6)] overflow-hidden"
    >
      {/* inner double-stroke */}
      <div
        aria-hidden
        className="absolute inset-1 rounded-sm border border-amber-300/35"
      />
      {/* corner flame etches */}
      <div
        aria-hidden
        className="absolute top-0 left-0 h-3 w-3 border-t-2 border-l-2 border-amber-400/80 rounded-tl"
      />
      <div
        aria-hidden
        className="absolute top-0 right-0 h-3 w-3 border-t-2 border-r-2 border-amber-400/80 rounded-tr"
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-0 h-3 w-3 border-b-2 border-l-2 border-amber-400/80 rounded-bl"
      />
      <div
        aria-hidden
        className="absolute bottom-0 right-0 h-3 w-3 border-b-2 border-r-2 border-amber-400/80 rounded-br"
      />
      {/* soft phoenix glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          backgroundImage:
            "radial-gradient(circle at 50% 60%, rgba(234,88,12,0.18) 0px, transparent 55%)",
        }}
      />
    </div>
  );
}

function Pedestal() {
  return (
    <div
      className="relative flex items-end justify-center min-h-[520px]"
      data-testid="wardrobe-pedestal"
    >
      {/* vertical ambient flame plume behind the pedestal */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-6 bottom-24 opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(ellipse at 50% 100%, rgba(234,88,12,0.35) 0px, rgba(234,88,12,0.1) 25%, transparent 60%)," +
            "radial-gradient(ellipse at 50% 80%, rgba(109,40,217,0.18) 0px, transparent 55%)",
        }}
      />

      {/* purple velvet drape backdrop */}
      <div
        aria-hidden
        className="absolute inset-x-6 top-2 bottom-28 rounded-2xl border border-purple-900/50 bg-gradient-to-b from-purple-950/40 via-black/40 to-rose-950/40 shadow-[inset_0_0_60px_rgba(0,0,0,0.75)]"
      />

      {/* pedestal disc */}
      <div className="relative mb-6">
        <div className="h-16 w-72 rounded-full bg-gradient-to-b from-amber-700 via-amber-900 to-black border-2 border-amber-400/70 shadow-[0_14px_40px_rgba(190,80,30,0.55),inset_0_2px_0_rgba(255,230,180,0.5)]" />
        <div className="h-3 w-72 -mt-2 rounded-b-full bg-gradient-to-b from-black/80 to-transparent" />
        {/* ornate rim detail */}
        <div className="pointer-events-none absolute inset-x-0 top-2 h-2 mx-auto w-72 flex items-center justify-between px-6">
          {[0, 1, 2, 3, 4].map((i) => (
            <span key={i} className="h-2 w-2 rounded-full bg-amber-300/80 shadow-[0_0_6px_rgba(252,211,77,0.9)]" />
          ))}
        </div>
      </div>
    </div>
  );
}
